/**
 * The shared shell for the three template tabs (#2624).
 *
 * Inspection templates, checklist templates and work paths each had their own
 * copy of the same ~75 lines: heading, create button, show-inactive switch,
 * list, dialog. Normalising the names showed the files were identical apart
 * from which list and dialog they mounted and which i18n namespace they read.
 *
 * Only the shell is shared. The list and the dialog stay with their feature,
 * because that is where the real differences live — a work path task editor
 * has nothing in common with an inspection measurement editor.
 *
 * Labels come in as explicit keys rather than being derived from a namespace,
 * since the three do not agree on naming: two use `template.*`, inspections
 * use `inspectionTemplates.*` with `showHidden` instead of `showInactive`.
 * Renaming them would be a separate change with its own risk.
 */

import { useState, type ReactNode } from 'react';
import { Box, Button, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

interface Props {
  title: string;
  subtitle: string;
  createLabel: string;
  showInactiveLabel: string;
  /** Rendered with the current filter state; owns its own data fetching. */
  renderList: (includeInactive: boolean) => ReactNode;
  /** Rendered always; the tab only tells it whether it should be open. */
  renderDialog: (open: boolean, onClose: () => void) => ReactNode;
  /** Set when an existing template is being edited, cleared on close. */
  isEditing: boolean;
  /** Clears the feature's own edit state; the layout owns the create flag. */
  onCloseDialog: () => void;
}

export function TemplateTabLayout({
  title,
  subtitle,
  createLabel,
  showInactiveLabel,
  renderList,
  renderDialog,
  isEditing,
  onCloseDialog,
}: Props) {
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  const handleClose = () => {
    setCreateDialogOpen(false);
    onCloseDialog();
  };

  return (
    <Box>
      <Stack spacing={3}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h6" gutterBottom>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            {createLabel}
          </Button>
        </Box>

        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                size="small"
              />
            }
            label={showInactiveLabel}
          />
        </Box>

        {renderList(showInactive)}

        {renderDialog(createDialogOpen || isEditing, handleClose)}
      </Stack>
    </Box>
  );
}
